import { createFileRoute } from '@tanstack/react-router'
import { LEGAL_CONFIG, LEGAL_VERSIONS } from '@/legal.config'
import { LegalVersionMeta } from '@/routes/legal/-legal-version-meta'

export const Route = createFileRoute('/legal/_layout/cookies')({
  component: CookiePolicyPage,
})

function CookiePolicyPage() {
  return (
    <article className="prose prose-neutral dark:prose-invert max-w-none">
      <h1>Cookie Policy</h1>
      <LegalVersionMeta
        current={LEGAL_VERSIONS.cookies.current}
        history={LEGAL_VERSIONS.cookies.history}
      />

      <p>
        This policy explains what cookies and similar technologies{' '}
        {LEGAL_CONFIG.companyName} uses on {LEGAL_CONFIG.websiteUrl} and the{' '}
        {LEGAL_CONFIG.productName} application, why we use them, and how you can
        control them.
      </p>

      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files placed on your device by a website. They
        allow the site to remember your actions and preferences over a period of
        time. Similar technologies include local storage, session storage, and
        tracking pixels.
      </p>

      <h2>2. Categories we use</h2>

      <h3>Strictly necessary</h3>
      <p>
        Required for the Service to function. These cannot be disabled without
        breaking core features such as authentication and bot protection.
      </p>

      <h3>Analytics</h3>
      <p>
        Help us understand how the Service is used so we can improve it. No
        personally identifiable information is shared with third parties for
        advertising purposes.
      </p>

      <h3>Error monitoring</h3>
      <p>
        Capture information about errors and performance issues so we can fix
        bugs and maintain reliability.
      </p>

      <h2>3. Cookies in detail</h2>

      <h3>Strictly necessary</h3>
      <table>
        <thead>
          <tr>
            <th>Name / pattern</th>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>better-auth.*</code>
            </td>
            <td>{LEGAL_CONFIG.productName} (Better Auth)</td>
            <td>Session authentication token</td>
            <td>Session / configurable</td>
          </tr>
          <tr>
            <td>
              <code>cf_clearance</code>
            </td>
            <td>Cloudflare</td>
            <td>
              Records that the Turnstile CAPTCHA challenge was passed, used to
              allow access to protected endpoints
            </td>
            <td>30 minutes</td>
          </tr>
          <tr>
            <td>
              <code>__cf_bm</code>
            </td>
            <td>Cloudflare</td>
            <td>
              Bot management — distinguishes humans from automated traffic
            </td>
            <td>30 minutes</td>
          </tr>
        </tbody>
      </table>

      <h3>Analytics</h3>
      <table>
        <thead>
          <tr>
            <th>Name / pattern</th>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>ph_*</code>
            </td>
            <td>PostHog</td>
            <td>
              Product analytics — tracks feature usage, page views, and user
              flows to help us improve the product
            </td>
            <td>1 year</td>
          </tr>
          <tr>
            <td>
              <code>_ga</code>
            </td>
            <td>Google Analytics (via GTM)</td>
            <td>Distinguishes unique users for aggregate traffic analysis</td>
            <td>2 years</td>
          </tr>
          <tr>
            <td>
              <code>_ga_*</code>
            </td>
            <td>Google Analytics (via GTM)</td>
            <td>Session persistence for GA4 measurement</td>
            <td>2 years</td>
          </tr>
          <tr>
            <td>
              <code>_gid</code>
            </td>
            <td>Google Analytics (via GTM)</td>
            <td>Distinguishes users for daily traffic reporting</td>
            <td>24 hours</td>
          </tr>
        </tbody>
      </table>
      <p>
        Analytics cookies are only set in production and only when the relevant
        API keys are configured. They are not set in development environments or
        during candidate interview sessions.
      </p>

      <h3>Error monitoring</h3>
      <table>
        <thead>
          <tr>
            <th>Name / pattern</th>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>sentry-sc</code>, local storage keys
            </td>
            <td>Sentry</td>
            <td>
              Error tracking and performance monitoring — captures stack traces
              and diagnostic data when a JavaScript error occurs
            </td>
            <td>Session</td>
          </tr>
        </tbody>
      </table>

      <h2>4. Cookies not set during candidate interviews</h2>
      <p>
        Analytics and tag-management cookies (PostHog, Google Tag Manager) are
        not loaded on the candidate interview pages (
        <code>/interview/:token</code>). Only strictly necessary session and
        security cookies operate during an interview.
      </p>

      <h2>5. How to manage cookies</h2>
      <p>
        You can control cookies through your browser settings. Disabling
        strictly necessary cookies will prevent you from logging in or
        completing a bot-protection challenge. Guidance for common browsers:
      </p>
      <ul>
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
            rel="noreferrer"
          >
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
            target="_blank"
            rel="noreferrer"
          >
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
            target="_blank"
            rel="noreferrer"
          >
            Apple Safari
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-168dab11-0753-043d-7c16-ede5947fc64d"
            target="_blank"
            rel="noreferrer"
          >
            Microsoft Edge
          </a>
        </li>
      </ul>

      <h2>6. Changes to this policy</h2>
      <p>
        We may update this policy when we add or remove third-party services.
        The effective date at the top of the page will reflect the latest
        revision.
      </p>

      <h2>7. Contact</h2>
      <p>
        Questions about cookies:{' '}
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
      </p>
    </article>
  )
}
